/**
 * Tiled icon patterns for profile backgrounds.
 *
 * These are authored as SVG paths rather than emoji, and that isn't a style
 * preference — the shop's preview images are rasterised from these same
 * definitions by `sharp` (librsvg), which has no colour-emoji font. An emoji
 * pattern renders as white blobs there, so every preview would show something
 * the player doesn't get. Paths render identically in a browser and in the
 * generator, and they take their colour from the theme instead of carrying
 * their own.
 *
 * Every icon is drawn in a 24x24 box so one placement routine fits them all.
 *
 * A tile repeats seamlessly by drawing the icon at the centre *and* at all
 * four corners: the corner copies are each three-quarters outside the tile, so
 * abutting tiles complete each other.
 */

/** Icon paths, each in a 24x24 box. Silhouettes — they render at ~10% alpha. */
export const ICONS = Object.freeze({
    spade: ['M12 2C12 2 4 9 4 14a4 4 0 0 0 7 2.8L10 22h4l-1-5.2A4 4 0 0 0 20 14c0-5-8-12-8-12z'],
    heart: ['M12 21S3 14.5 3 8.8A4.5 4.5 0 0 1 12 6.2 4.5 4.5 0 0 1 21 8.8C21 14.5 12 21 12 21z'],
    diamond: ['M12 2 20 12 12 22 4 12z'],
    club: [
        'M12 2a4 4 0 0 1 3 6.6A4 4 0 1 1 17 16a4.2 4.2 0 0 1-4-1.2L14 22h-4l1-7.2A4.2 4.2 0 0 1 7 16 4 4 0 1 1 9 8.6 4 4 0 0 1 12 2z',
    ],
    dice: [
        'M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z',
    ],
    crown: ['M3 18 5 7l4 4.5L12 4l3 7.5L19 7l2 11z'],
    star: ['M12 2.5 14.9 9l7.1.7-5.3 4.8 1.5 7-6.2-3.6L5.8 21.5l1.5-7L2 9.7 9.1 9z'],
    pawn: [
        'M12 2a4 4 0 0 1 2.4 7.2c1 .9 1.4 2.3 1.1 3.8l-.6 2.6h-5.8l-.6-2.6c-.3-1.5.1-2.9 1.1-3.8A4 4 0 0 1 12 2zM7 17h10l1.5 5h-13z',
    ],
    cube: ['M12 2 22 7.5v9L12 22 2 16.5v-9zM12 4.6 4.6 8.6 12 12.6l7.4-4z'],
    pipe: ['M4 4h7v6H10a4 4 0 0 0 4 4v-1h6v7h-6v-1a10 10 0 0 1-10-10V4z'],
    rocket: [
        'M12 1.5c3.4 2.6 5.2 6.4 5.2 10.6l2.3 2.6-2.6 1 .3 4.4-3.6-2.3h-3.2L6.8 20l.3-4.4-2.6-1 2.3-2.6C6.8 7.9 8.6 4.1 12 1.5zM12 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4z',
    ],
    // Ring drawn as an elliptical annulus: a stroked arc would vanish, since
    // these are filled silhouettes with no stroke.
    planet: [
        'M12 4.5a6.8 6.8 0 1 1 0 13.6 6.8 6.8 0 0 1 0-13.6z',
        'M1.5 14.2c0-1.1 4.7-2 10.5-2s10.5.9 10.5 2-4.7 2-10.5 2-10.5-.9-10.5-2zm10.5-.8c-4.6 0-8.3.4-8.3.8s3.7.8 8.3.8 8.3-.4 8.3-.8-3.7-.8-8.3-.8z',
    ],
    ankh: [
        'M12 2a4.5 4.5 0 0 1 3 7.8L14 11h5v2.4h-5V22h-4v-8.6H5V11h5l-1-1.2A4.5 4.5 0 0 1 12 2zm0 2.4a2.2 2.2 0 0 0 0 4.4 2.2 2.2 0 0 0 0-4.4z',
    ],
    pyramid: ['M12 2 23 21H1zM12 7 6.5 18h11z'],
    // Greek key, as three nested Ls — a stroked spiral wouldn't survive being
    // filled, and at pattern scale the nesting is what reads.
    meander: [
        'M2 2h20v3.4H5.4V22H2z',
        'M7.6 7.6h11V22h-3.4V11H7.6z',
        'M13.2 13.2h5.4v3.4h-2v2.6h-3.4z',
    ],
    // Open-ended arcs rather than a closed ring, so it reads as a brushstroke
    // curling inward the way Van Gogh's skies do.
    swirl: [
        'M12 1.6a10.4 10.4 0 1 0 9.9 13.6 1.9 1.9 0 0 0-3.6-1.1A6.6 6.6 0 1 1 12 5.4a1.9 1.9 0 0 0 0-3.8z',
        'M12 8.2a3.9 3.9 0 1 0 3.7 5.1 1.6 1.6 0 0 0-3-1 .9.9 0 1 1-.7-1.2 1.6 1.6 0 0 0 0-2.9z',
    ],
    honey: [
        'M7 2h10v2H7zM6 6h12l1.6 12.4A3 3 0 0 1 16.6 22H7.4a3 3 0 0 1-3-3.6z',
    ],
    gem: ['M7 3h10l4 6-9 12L3 9zM7.6 5 5.4 8.6h13.2L16.4 5z'],
    bolt: ['M13.5 1.5 4 13.6h6.2L9.5 22.5 20 10.4h-6.2z'],
    leaf: ['M20 3C10 3 3 8 3 15a6 6 0 0 0 1.5 4C6 13 10 9 16 7c-4 3-7 6-9 12a6 6 0 0 0 4 1c7 0 9-8 9-17z'],
    moon: ['M15 2a10 10 0 1 0 7 15A9 9 0 0 1 15 2z'],
    bubble: ['M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18zm-2.6 3.4a3.4 3.4 0 0 0-2.8 3.2c0 .6 1.4.6 1.6 0a2 2 0 0 1 1.6-1.8c.7-.2.5-1.6-.4-1.4z'],

    // ── Added with the second wave of themes ─────────────────────────────
    wave: ['M0 12.5c4-5 8-5 12 0s8 5 12 0v5c-4 5-8 5-12 0s-8-5-12 0z'],
    mushroom: [
        'M12 2.5c5.2 0 9.4 3.6 9.4 7.2 0 .8-.7 1.3-1.6 1.3H4.2c-.9 0-1.6-.5-1.6-1.3C2.6 6.1 6.8 2.5 12 2.5z',
        'M9.4 12.6h5.2l.9 8.9H8.5z',
    ],
    snowflake: [
        'M11 1.2h2v21.6h-2z',
        'M2.1 6.3l1-1.73 18.7 10.8-1 1.73z',
        'M21.9 6.3l1 1.73-18.7 10.8-1-1.73z',
    ],
    flame: [
        'M12.6 1.5c2.8 4.2.6 6.3 2.7 9.2 1.6 2.2 1.2 5.4-1 7.2-1.9 1.5-4.9 1.3-6.7-.5-2.3-2.3-1.9-5.9.4-8-.9 3.1 1 4.2 1 4.2.3-3.6-1.4-8.5 3.6-12.1z',
    ],
    skull: [
        'M12 2a9 9 0 0 1 9 9c0 3-1.6 5.2-3.2 6.3V21h-2.6v-2H8.8v2H6.2v-3.7C4.6 16.2 3 14 3 11a9 9 0 0 1 9-9zm-3.4 8.2a2.1 2.1 0 1 0 0 4.2 2.1 2.1 0 0 0 0-4.2zm6.8 0a2.1 2.1 0 1 0 0 4.2 2.1 2.1 0 0 0 0-4.2z',
    ],
    // Four petals around a gap. Earlier versions had them meeting in the
    // middle and merged into one blob at tile scale — the gap is what makes
    // it read as a flower rather than a smudge.
    lotus: [
        'M12 1.8c1.7 2.3 2.5 4.2 2.5 5.7s-.8 2.8-2.5 3.8c-1.7-1-2.5-2.3-2.5-3.8S10.3 4.1 12 1.8z',
        'M2.2 8.7c2.7.4 4.6 1.5 5.8 3.1.9 1.3.9 2.7 0 4.4-2-.4-3.5-1.5-4.4-3.2-.9-1.6-1.3-3-1.4-4.3z',
        'M21.8 8.7c-.1 1.3-.5 2.7-1.4 4.3-.9 1.7-2.4 2.8-4.4 3.2-.9-1.7-.9-3.1 0-4.4 1.2-1.6 3.1-2.7 5.8-3.1z',
        'M12 13.1c1.7 1 2.5 2.3 2.5 3.8s-.8 3.4-2.5 5.7c-1.7-2.3-2.5-4.2-2.5-5.7s.8-2.8 2.5-3.8z',
    ],
    circuit: [
        'M2.5 2.5h7v2.2H4.7v4.8H2.5z',
        'M21.5 2.5v7h-2.2V4.7h-4.8V2.5z',
        'M2.5 21.5v-7h2.2v4.8h4.8v2.2z',
        'M21.5 21.5h-7v-2.2h4.8v-4.8h2.2z',
        'M10.4 10.4h3.2v3.2h-3.2z',
    ],
    scroll: [
        'M6 2.5h12a2.4 2.4 0 0 1 2.4 2.4v14.2A2.4 2.4 0 0 1 18 21.5H6a2.4 2.4 0 0 1-2.4-2.4V4.9A2.4 2.4 0 0 1 6 2.5zm2.2 4.3v2.1h7.6V6.8zm0 4.2v2.1h7.6V11zm0 4.2v2.1h4.8v-2.1z',
    ],
    ghost: [
        'M12 1.8a8.2 8.2 0 0 1 8.2 8.2v12.2l-2.7-2-2.7 2-2.8-2-2.8 2-2.7-2-2.7 2V10A8.2 8.2 0 0 1 12 1.8zM9.1 8.6a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2zm5.8 0a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2z',
    ],
    cloud: ['M7 19.5a5.4 5.4 0 0 1-.5-10.8 7 7 0 0 1 13.3 2.3 4.5 4.5 0 0 1-1.3 8.5z'],
    sword: [
        'M12 1.2 15.3 6.6v8.8H8.7V6.6z',
        'M6.8 15.9h10.4v2.4H6.8z',
        'M10.8 18.7h2.4v4.1h-2.4z',
    ],
    mountain: ['M1.5 20.8 9 6.4l3.9 6 2.1-3.2 7.5 11.6z'],
    shield: ['M12 1.6 21 4.7v7.1c0 5-3.7 9.2-9 10.6-5.3-1.4-9-5.6-9-10.6V4.7z'],
    compass: [
        'M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20zm0 2.4a7.6 7.6 0 1 0 0 15.2 7.6 7.6 0 0 0 0-15.2z',
        'M16 8 13.2 13.2 8 16l2.8-5.2z',
    ],
    key: [
        'M15.4 2a6.9 6.9 0 1 1-6.2 9.9L7.6 13.5v2.7H4.9v2.7H2.2v-3.6l7.3-7.3A6.9 6.9 0 0 1 15.4 2zm1.4 3a2 2 0 1 0 0 4 2 2 0 0 0 0-4z',
    ],
    // A horn, a mane and a sparkle. A whole unicorn is a smudge at tile
    // scale; the horn is the part anyone recognises, and the mane sweep is
    // what stops it reading as a plain traffic cone.
    unicorn: [
        'M12.4 1.2 16.4 13H8.4z',
        'M8.6 13.6c-2.9 2-4.6 5-5 8.9 3-1.6 5.3-3.9 6.8-6.9z',
        'M19 14.2l1.1 2.5 2.5 1.1-2.5 1.1-1.1 2.5-1.1-2.5-2.5-1.1 2.5-1.1z',
    ],
})

const CORNERS = [[0, 0], [100, 0], [0, 100], [100, 100]]

/**
 * One seamlessly-tiling tile as an SVG string.
 *
 * @param {object} spec
 * @param {string} spec.icon Key into ICONS.
 * @param {string} [spec.color] Any CSS colour, including rgba.
 * @param {number} [spec.scale] Icon size as a fraction of the tile.
 * @param {number} [spec.angle] Rotation, degrees. Diagonal reads best.
 * @param {number} [spec.size] Tile size in px (the SVG's own width/height).
 */
export function patternTileSvg(spec) {
    const icon = spec.icon
    const color = spec.color || 'rgba(255,255,255,0.10)'
    const scale = spec.scale || 0.34
    const angle = spec.angle === undefined ? -18 : spec.angle
    const size = spec.size || 72

    const paths = ICONS[icon]
    if (!paths) return ''

    // The icon is authored in a 24-unit box; `k` maps that onto `scale` of a
    // 100-unit tile.
    const k = (100 * scale) / 24
    const place = (x, y) =>
        `<g transform="translate(${x} ${y}) rotate(${angle}) scale(${k}) translate(-12 -12)">` +
        paths.map(d => `<path d="${d}"/>`).join('') +
        '</g>'

    const body = [place(50, 50)].concat(CORNERS.map(c => place(c[0], c[1]))).join('')

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 100 100">` +
        `<g fill="${color}">${body}</g></svg>`
}

/**
 * The same tile as a CSS `url(...)` value.
 *
 * Percent-encoded rather than base64: it stays readable in devtools, and it's
 * shorter for markup this small.
 */
export function patternLayer(spec) {
    const svg = patternTileSvg(spec)
    if (!svg) return ''
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
}

/**
 * A full CSS `background` value: the pattern over a base fill.
 *
 * Returns the base untouched when there's no pattern, so a common-tier theme
 * (which is only a colour) goes through the same code path as the rest.
 */
export function layeredBackground(base, spec) {
    if (!spec) return base
    const layer = patternLayer(spec)
    return layer ? `${layer}, ${base}` : base
}

/** The matching `background-size`, since the pattern layer needs a tile size. */
export function layeredBackgroundSize(spec) {
    if (!spec) return null
    const size = spec.size || 72
    return `${size}px ${size}px, cover`
}
